// src/services/masjidService.js
import { apiClient } from "./apiClient";
import { getCached, setCached, isStale, broadcastSet, peekCached } from "../shared/cache/dataCache";
//import { todayISO } from "../shared/utils/datetime";

// ====== existing code (your functions) stays as-is above/below ======
// e.g., getPrayerTimes, fetchHijriDate, etc.

// ---------------------------
// Cache config (tune as needed)
// ---------------------------
const PRAYER_TTL = 5 * 60 * 1000;     // considered "fresh" for 5 min
const PRAYER_STALE = 60 * 1000;       // after 1 min, refresh quietly
const ANN_TTL = 10 * 60 * 1000;
const ANN_STALE = 2 * 60 * 1000;
const EVENTS_TTL = 15 * 60 * 1000;
const EVENTS_STALE = 3 * 60 * 1000;

// If your API serves different content per tenant/day, include them in the key:
function todayISO() {
  // local date is fine for most cases; swap for TZ-aware if needed
  return new Date().toISOString().slice(0, 10);
}
const kPrayer = (masjidId) => `prayer:${masjidId}:${todayISO()}`;
const kAnn   = (masjidId) => `announcements:${masjidId}`;
const kEvt   = (masjidId) => `events:${masjidId}:${todayISO()}`;

// ---------------------------
// PRAYER (cached)
// ---------------------------
async function fetchPrayerTimesFresh(masjidId) {
  const res = await apiClient.get(`/masjid/${masjidId}`, {
    params: { t: Date.now() }, // avoid intermediate CDN caching
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  });
  // Optionally normalize/validate here
  return res.data;
}

async function refreshPrayerTimes(masjidId, key) {
  const fresh = await fetchPrayerTimesFresh(masjidId);
  setCached(key, fresh, PRAYER_TTL);
  broadcastSet(key);
  return fresh;
}

/**
 * getPrayerTimesCached:
 * - returns cached immediately (no flicker)
 * - silently refreshes if stale (keeps last good UI)
 */
export async function getPrayerTimesCached(masjidId, { background = true } = {}) {
  const key = kPrayer(masjidId);
  const cached = getCached(key);
  const shouldRefresh = isStale(key, PRAYER_STALE);

  if (background && shouldRefresh) {
    // fire-and-forget; calling component keeps showing cached UI
    refreshPrayerTimes(masjidId, key).catch(() => {});
  }

  if (cached) {
    return { data: cached, fromCache: true };
  }

  // cold start: fetch once and cache
  try {
    const fresh = await refreshPrayerTimes(masjidId, key);
    return { data: fresh, fromCache: false };
  } catch (err) {
    // surface a consistent error object; caller decides UI fallback
    return { data: null, error: err, fromCache: false };
  }
}


// Synchronous read used by UI to avoid flicker on remounts/navigation.
export function peekPrayerTimes(masjidId) {
  return peekCached(kPrayer(masjidId));
}

// ---------------------------
// ANNOUNCEMENTS (cached) — adjust endpoints to match your API
// ---------------------------
async function fetchAnnouncementsFresh(masjidId) {
  const res = await apiClient.get(`/announcements/${masjidId}`, {
    params: { t: Date.now() },
  });
  return res.data;
}

export async function getAnnouncementsCached(masjidId, { background = true } = {}) {
  const key = kAnn(masjidId);
  const cached = getCached(key);
  const shouldRefresh = isStale(key, ANN_STALE);

  if (background && shouldRefresh) {
    (async () => {
      try {
        const fresh = await fetchAnnouncementsFresh(masjidId);
        setCached(key, fresh, ANN_TTL);
        broadcastSet(key);
      } catch {}
    })();
  }

  if (cached) return { data: cached, fromCache: true };

  try {
    const fresh = await fetchAnnouncementsFresh(masjidId);
    setCached(key, fresh, ANN_TTL);
    broadcastSet(key);
    return { data: fresh, fromCache: false };
  } catch (error) {
    return { data: null, error, fromCache: false };
  }
}

// ---------------------------
// EVENTS (cached) — adjust endpoints accordingly
// ---------------------------
async function fetchEventsFresh(masjidId) {
  const res = await apiClient.get(`/events/${masjidId}`, {
    params: { t: Date.now() },
  });
  return res.data;
}

export async function getEventsCached(masjidId, { background = true } = {}) {
  const key = kEvt(masjidId);
  const cached = getCached(key);
  const shouldRefresh = isStale(key, EVENTS_STALE);

  if (background && shouldRefresh) {
    (async () => {
      try {
        const fresh = await fetchEventsFresh(masjidId);
        setCached(key, fresh, EVENTS_TTL);
        broadcastSet(key);
      } catch {}
    })();
  }

  if (cached) return { data: cached, fromCache: true };

  try {
    const fresh = await fetchEventsFresh(masjidId);
    setCached(key, fresh, EVENTS_TTL);
    broadcastSet(key);
    return { data: fresh, fromCache: false };
  } catch (error) {
    return { data: null, error, fromCache: false };
  }
}




// Back-compat alias: same signature as before, returns plain data
export async function getPrayerTimes(masjidId, opts) {
  const { data } = await getPrayerTimesCached(masjidId, opts);
  return data;
}

// (Optional) add similar shims if you used these old names in other files:
export async function getAnnouncements(masjidId, opts) {
  const { data } = await getAnnouncementsCached(masjidId, opts);
  return data;
}
export async function getEvents(masjidId, opts) {
  const { data } = await getEventsCached(masjidId, opts);
  return data;
}

